import {
  geoContains,
  geoDistance,
  geoGraticule10,
  geoInterpolate,
  geoOrthographic,
  geoPath,
} from "d3-geo";
import { feature } from "topojson-client";
import land110m from "@/data/land-110m.json";

const W = 1200;
const H = 1700;
const GOLD = "#D4AF37";
const AMBER = "#FF9F1C";
const INK = "#0B0C10";

const land = feature(land110m, land110m.objects.countries);

function centroid(stops) {
  let x = 0;
  let y = 0;
  let z = 0;
  stops.forEach((s) => {
    const la = (s.lat * Math.PI) / 180;
    const lo = (s.lng * Math.PI) / 180;
    x += Math.cos(la) * Math.cos(lo);
    y += Math.cos(la) * Math.sin(lo);
    z += Math.sin(la);
  });
  const lng = (Math.atan2(y, x) * 180) / Math.PI;
  const lat = (Math.atan2(z, Math.hypot(x, y)) * 180) / Math.PI;
  return [lng, Math.max(-55, Math.min(55, lat))];
}

async function ensureFonts() {
  if (!document.fonts) return;
  await Promise.all([
    document.fonts.load('600 92px "Cormorant Garamond"'),
    document.fonts.load('italic 500 92px "Cormorant Garamond"'),
    document.fonts.load('400 22px "IBM Plex Mono"'),
    document.fonts.load('300 26px "Manrope"'),
  ]);
}

export async function renderPoster(stops) {
  await ensureFonts();
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  /* background + vignette + grain */
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);
  const vg = ctx.createRadialGradient(W / 2, H * 0.45, 100, W / 2, H * 0.45, H * 0.8);
  vg.addColorStop(0, "rgba(212,175,55,0.07)");
  vg.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(255,255,255,0.035)";
  for (let i = 0; i < 9000; i++) {
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }

  /* frame */
  ctx.strokeStyle = "rgba(212,175,55,0.35)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(48, 48, W - 96, H - 96);

  /* header */
  ctx.fillStyle = GOLD;
  ctx.font = '500 20px "IBM Plex Mono"';
  ctx.textBaseline = "alphabetic";
  ctx.letterSpacing = "8px";
  ctx.fillText("AETHERIA · CHRONO-ATLAS", 96, 136);
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(248,250,252,0.45)";
  ctx.fillText(`EXPEDITION · ${String(stops.length).padStart(2, "0")} STOPS`, W - 96, 136);
  ctx.textAlign = "left";
  ctx.letterSpacing = "0px";

  ctx.fillStyle = "#F8FAFC";
  ctx.font = '500 96px "Cormorant Garamond"';
  ctx.fillText("Every stone", 96, 260);
  ctx.fillText("keeps a ", 96, 360);
  const w = ctx.measureText("keeps a ").width;
  ctx.fillStyle = GOLD;
  ctx.font = 'italic 500 96px "Cormorant Garamond"';
  ctx.fillText("secret.", 96 + w, 360);

  /* globe */
  const cx = W / 2;
  const cy = 880;
  const r = 400;
  const [clng, clat] = centroid(stops);
  const proj = geoOrthographic()
    .translate([cx, cy])
    .scale(r)
    .rotate([-clng, -clat])
    .clipAngle(90);
  const path = geoPath(proj, ctx);
  const center = [clng, clat];
  const visible = (p) => geoDistance(p, center) < Math.PI / 2 - 0.02;

  const halo = ctx.createRadialGradient(cx, cy, r * 0.9, cx, cy, r * 1.35);
  halo.addColorStop(0, "rgba(212,175,55,0.28)");
  halo.addColorStop(0.5, "rgba(212,175,55,0.06)");
  halo.addColorStop(1, "rgba(212,175,55,0)");
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.35, 0, Math.PI * 2);
  ctx.fill();

  const sphere = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r);
  sphere.addColorStop(0, "#161926");
  sphere.addColorStop(1, "#0b0d14");
  ctx.fillStyle = sphere;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  path(geoGraticule10());
  ctx.strokeStyle = "rgba(212,175,55,0.12)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "rgba(212,175,55,0.85)";
  for (let lat = -84; lat <= 84; lat += 1.5) {
    const rad = Math.cos((lat * Math.PI) / 180);
    const step = Math.min(6, 1.5 / Math.max(rad, 0.22));
    for (let lng = -180; lng < 180; lng += step) {
      const p = [lng, lat];
      if (!visible(p) || !geoContains(land, p)) continue;
      const xy = proj(p);
      if (!xy) continue;
      const edge = 1 - geoDistance(p, center) / (Math.PI / 2);
      ctx.globalAlpha = 0.35 + 0.65 * Math.min(1, edge * 2.5);
      ctx.beginPath();
      ctx.arc(xy[0], xy[1], 2.1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(212,175,55,0.55)";
  ctx.lineWidth = 2;
  ctx.stroke();

  /* route arcs */
  ctx.save();
  ctx.shadowColor = "rgba(212,175,55,0.8)";
  ctx.shadowBlur = 14;
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 2.5;
  for (let k = 0; k < stops.length - 1; k++) {
    const a = [stops[k].lng, stops[k].lat];
    const b = [stops[k + 1].lng, stops[k + 1].lat];
    const interp = geoInterpolate(a, b);
    ctx.beginPath();
    let pen = false;
    for (let j = 0; j <= 72; j++) {
      const p = interp(j / 72);
      const xy = visible(p) ? proj(p) : null;
      if (!xy) {
        pen = false;
        continue;
      }
      if (pen) ctx.lineTo(xy[0], xy[1]);
      else ctx.moveTo(xy[0], xy[1]);
      pen = true;
    }
    ctx.stroke();
  }
  ctx.restore();

  /* beacons */
  ctx.font = '500 18px "IBM Plex Mono"';
  stops.forEach((s, i) => {
    const p = [s.lng, s.lat];
    if (!visible(p)) return;
    const [x, y] = proj(p);
    ctx.beginPath();
    ctx.arc(x, y, 14, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,159,28,0.7)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, y, 6, 0, Math.PI * 2);
    ctx.fillStyle = AMBER;
    ctx.fill();
    ctx.fillStyle = "#F8FAFC";
    ctx.fillText(String(i + 1).padStart(2, "0"), x + 20, y - 12);
  });

  /* stop list */
  const top = 1340;
  const cols = stops.length > 6 ? 2 : 1;
  const colW = (W - 192) / cols;
  const rows = Math.ceil(stops.length / cols);
  const rowH = Math.min(52, 300 / rows);
  stops.forEach((s, i) => {
    const c = Math.floor(i / rows);
    const rIdx = i % rows;
    const x = 96 + c * colW;
    const y = top + rIdx * rowH;
    ctx.fillStyle = GOLD;
    ctx.font = '500 16px "IBM Plex Mono"';
    ctx.fillText(String(i + 1).padStart(2, "0"), x, y);
    ctx.fillStyle = "#F8FAFC";
    ctx.font = `500 ${rowH > 40 ? 30 : 24}px "Cormorant Garamond"`;
    ctx.fillText(s.name, x + 44, y);
    if (cols === 1) {
      ctx.fillStyle = "rgba(248,250,252,0.45)";
      ctx.font = '400 15px "IBM Plex Mono"';
      ctx.textAlign = "right";
      ctx.fillText(s.location.toUpperCase(), W - 96, y);
      ctx.textAlign = "left";
    }
  });

  /* footer */
  ctx.strokeStyle = "rgba(212,175,55,0.3)";
  ctx.beginPath();
  ctx.moveTo(96, H - 120);
  ctx.lineTo(W - 96, H - 120);
  ctx.stroke();
  ctx.fillStyle = "rgba(248,250,252,0.4)";
  ctx.font = '400 15px "IBM Plex Mono"';
  ctx.letterSpacing = "4px";
  ctx.fillText("THE QUESTIONED EARTH — ALTERNATIVE READINGS PRESENTED AS CLAIMS, NOT FACT", 96, H - 92);
  ctx.fillStyle = GOLD;
  ctx.letterSpacing = "2px";
  ctx.fillText(`#route=${stops.map((s) => s.id).join(",")}&tour=1`, 96, H - 64);
  ctx.letterSpacing = "0px";

  return canvas.toDataURL("image/png");
}
